import { useEffect, useRef, useState } from 'react'
import Avatar from 'react-avatar'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { TApi, RECORD_TYPES } from '../../../../shared/const'
import http from '../../../../shared/http'
import { stateLabel } from '../../../../shared/helpers'
import { optionLabel } from '../../../../shared/optionList'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import useDialogDismiss from '../../../../shared/useDialogDismiss'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import ReminderNote from '../../../../UI/ReminderNote'
import RecordComments from '../../../../UI/RecordComments'
import { PlainTextarea } from '../../../../UI/PlainField'

import styles from './detailModal.module.scss'
import AuthAvatar from '../../../../UI/AuthAvatar'

const TABS = [
	{ key: 'profile', label: 'profileTab' },
	{ key: 'target', label: 'targetAudience' },
	{ key: 'contact', label: 'contactDetails' },
	{ key: 'collaboration', label: 'collaboration' },
	{ key: 'log', label: 'logTab' }
]

/*
 * Bausteine auf Modulebene und nicht innerhalb der Komponente: React sieht eine
 * beim Rendern erzeugte Funktion als neuen Typ an und baut den Inhalt bei jedem
 * Tastendruck neu auf - der Schreibzeiger spraenge dann aus der Nachricht.
 */
function Card({ title, hint, children }) {
	return (
		<section className={styles.card}>
			{title && <h3 className={styles.cardTitle}>{title}</h3>}
			{hint && <p className={styles.cardHint}>{hint}</p>}
			{children}
		</section>
	)
}

/**
 * Die Junior Companies, für die von hier aus ein Verlauf geführt werden kann.
 *
 * Das sind die bereits verbundenen **und** die noch anfragbaren: Eine Anmerkung
 * ist auch vor der ersten Anfrage sinnvoll ("kennt unsere Lehrkraft, zuerst
 * anrufen"). Schüler:innen haben genau eine, Lehrkräfte oft mehrere.
 *
 * Die verbundenen stehen vorne und tragen ihren Status mit: Betreut eine
 * Lehrkraft mehrere Junior Companies, ist genau eine davon diejenige, mit der
 * es etwas zu erzählen gibt — die anderen haben nie angefragt.
 */
function companyChoices(supporter) {
	const status = new Map(
		(supporter?.connections || []).filter(c => c.companyId).map(c => [String(c.companyId), c.status])
	)
	const all = [
		...(supporter?.connections || []).map(c => ({ id: c.companyId, name: c.companyName })),
		...(supporter?.availableCompanies || [])
	]

	return all
		.filter((c, index) => c.id && all.findIndex(other => other.id === c.id) === index)
		.map(c => ({ ...c, status: status.get(String(c.id)) || null }))
}

function TagList({ label, values }) {
	if (!values?.length) return null

	return (
		<div className={styles.block}>
			<span className={styles.blockLabel}>{label}</span>
			<div className={styles.tags}>
				{values.map(item => (
					<span key={item} className={styles.tag}>
						{item}
					</span>
				))}
			</div>
		</div>
	)
}

/**
 * Detailprofil einer Expert:in für Lehrkräfte und Schüler:innen.
 *
 * Deckt "Profile öffnen" aus Spec Teil 2 B.2 ab. Zeigt ausschließlich Felder
 * aus dem schulischen Blickwinkel — der Server liefert unter diesem Zugang
 * ohnehin keine Kontaktdaten und keine internen Status.
 *
 * Der Inhalt steht in fünf Reitern (Profil, Zielgruppe, Kontakt,
 * Zusammenarbeit, Log-Verlauf). Vorher stand alles untereinander, das Fenster
 * wuchs mit dem Inhalt und sprang je nach Person an eine andere Stelle. Deshalb
 * hat es jetzt eine feste Größe; gescrollt wird im Inhalt.
 *
 * Der Log-Verlauf gehört der eigenen Junior Company: Was dort steht, sieht nur
 * sie (ihre Schüler:innen und die betreuenden Lehrkräfte) — nicht die
 * Expert:in, nicht eine andere Junior Company und nicht die Verwaltung.
 *
 * Die Anfrage wird im Reiter "Kontakt" gestellt. Seit Anforderung #232 sagt
 * sie immer, wobei die Junior Company Unterstützung sucht (Pflichtfeld), und
 * auf Wunsch, welche Kompetenzen der Expert:in sie ansprechen will — der
 * Anfrage-Knopf an der Kachel öffnet deshalb dieses Fenster gleich dort.
 */
export const REQUEST_MESSAGE_MIN = 20

export default function SupporterDetailModal({
	supporterId,
	defaultCompanyId,
	allowRequest,
	initialTab,
	onClose,
	onRequested
}) {
	const { t, i18n } = useTranslation()

	// Zielbranchen sind englisch gespeichert, angezeigt gehört die deutsche Fassung.
	const branches = useSelector(state => state.options?.branch) || []

	const [supporter, setSupporter] = useState(null)
	const [tab, setTab] = useState('profile')

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)
	const [message, setMessage] = useState('')
	const [chosenCompetences, setChosenCompetences] = useState([])
	const [companyId, setCompanyId] = useState('')
	const [logCompanyId, setLogCompanyId] = useState('')
	const [isSending, setSending] = useState(false)

	useEffect(() => {
		if (!supporterId) return

		setSupporter(null)
		setTab(initialTab || 'profile')
		setMessage('')
		setChosenCompetences([])
		setCompanyId('')
		setLogCompanyId('')
		// Die Firma mitgeben: Nur davon haengt ab, ob „passt nicht zu …" dabei
		// steht — der Zugriff selbst nicht.
		http.get(TApi.COMMON_SUPPORTER_ID(supporterId), {
			params: defaultCompanyId ? { companyId: defaultCompanyId } : {}
		})
			.then(({ data }) => {
				setSupporter(data)
				// Gibt es nur eine mögliche Firma, ist die Auswahl keine Entscheidung
				// — dann wird sie gesetzt und das Feld gar nicht erst gezeigt.
				const available = data.availableCompanies || []
				if (available.length === 1) setCompanyId(String(available[0].id))
				// Sonst gilt die Firma, die in der Liste eingestellt ist: Das Fenster
				// soll nicht bei einer anderen landen als die Ansicht dahinter.
				else if (available.some(item => String(item.id) === String(defaultCompanyId)))
					setCompanyId(String(defaultCompanyId))

				/*
				 * Beim Verlauf entscheidet die Zusammenarbeit, nicht die Liste:
				 * Wer angefragt hat, hat auch den Verlauf. Stuende hier die in der
				 * Liste eingestellte Firma, sähe eine Lehrkraft mit mehreren Junior
				 * Companies „Noch keine Einträge", während Anfrage und Absage bei
				 * der Nachbarfirma stehen — genau so gemeldet am 23.08.2026.
				 * Die eingestellte Firma gilt weiter, wenn sie selbst eine
				 * Zusammenarbeit hat oder wenn es gar keine gibt.
				 */
				const forLog = companyChoices(data)
				const preferred = forLog.find(item => String(item.id) === String(defaultCompanyId))
				const firstWithHistory = forLog.find(item => item.status)
				const chosen = (preferred?.status && preferred) || firstWithHistory || preferred || forLog[0]
				if (chosen) setLogCompanyId(String(chosen.id))
			})
			.catch(() => {
				// Meldung kommt aus shared/http
				onClose()
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [supporterId])

	const backdropProps = useDialogDismiss(onClose, { enabled: Boolean(supporterId) })

	if (!supporterId) return null

	/*
	 * Liefert der Server die Felder aus #24 noch nicht — etwa im Zeitfenster
	 * zwischen zwei Deploys —, verhält sich die Ansicht wie zuvor: eine Anfrage
	 * bleibt möglich, es gibt nur keine Auswahl. Ein verschwundener
	 * Anfrage-Knopf wäre der schlechtere Ausfall.
	 */
	const hasCompanyInfo = Array.isArray(supporter?.availableCompanies)
	const availableCompanies = supporter?.availableCompanies || []
	/*
	 * `allowRequest` kommt aus der Rolle: Anfragen stellen Schüler:innen, die
	 * Lehrkraft begleitet sie dabei (siehe SupportersList). Sie sieht dasselbe
	 * Profil, denselben Status und denselben Verlauf — nur den Weg zur Anfrage
	 * nicht.
	 */
	const canRequest = allowRequest && (hasCompanyInfo ? availableCompanies.length > 0 : !supporter?.connectionStatus)
	const needsCompanyChoice = availableCompanies.length > 1
	const isConnected = supporter?.connectionStatus === 'CONNECTED'
	const connections = supporter?.connections || []
	const logCompanies = companyChoices(supporter)
	// Der Server nennt nur die Kennung der einladenden Junior Company - den Namen
	// kennt die Ansicht ohnehin, wenn es eine der eigenen ist. Ist es eine fremde,
	// bleibt es dabei: Wer wen kennt, geht andere Junior Companies nichts an.
	const invitedByOwnCompany = supporter?.invitedByCompanyId
		? logCompanies.find(c => c.id === supporter.invitedByCompanyId)
		: null
	const endableConnections = connections.length
		? connections.filter(c => c.status === 'CONNECTED')
		: isConnected
		? [{}]
		: []

	/*
	 * Beenden fragt nach: Es betrifft die andere Seite und löst eine Nachricht
	 * aus. Betreut eine Lehrkraft mehrere verbundene Firmen, sagt der Server,
	 * dass eine Auswahl fehlt — dann wird sie hier abgefragt (#24, #27).
	 */
	const endCollaboration = connection => {
		Swal.fire({
			title: t('endCollaborationTitle'),
			text: t('endCollaborationText', { company: connection.companyName || '' }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('endCollaboration'),
			cancelButtonText: t('Cancel'),
			confirmButtonColor: '#d33'
		}).then(result => {
			if (!result.isConfirmed) return
			setSending(true)
			http.post(TApi.COMMON_SUPPORTER_END(supporterId), { companyId: connection.companyId })
				.then(() => {
					toast.success(t('collaborationEnded'))
					onRequested()
				})
				.catch(() => {}) // Meldung kommt aus shared/http
				.finally(() => setSending(false))
		})
	}

	const toggleCompetence = name =>
		setChosenCompetences(current =>
			current.includes(name) ? current.filter(item => item !== name) : [...current, name]
		)

	const messageLength = message.trim().length
	const messageMissing = REQUEST_MESSAGE_MIN - messageLength

	const request = () => {
		setSending(true)
		http.post(TApi.COMMON_SUPPORTER_REQUEST(supporterId), {
			message: message.trim(),
			...(chosenCompetences.length ? { competences: chosenCompetences } : {}),
			...(companyId ? { companyId: Number(companyId) } : {})
		})
			.then(() => {
				toast.success(t('requestSent'))
				onRequested()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSending(false))
	}

	/*
	 * Die Fussleiste bleibt leer, wenn es weder einen Erinnerungsstand noch
	 * einen Weg zur Anfrage gibt - dann steht dort nur ein leerer Streifen. Die
	 * Bedingung bildet ab, wann ReminderNote etwas anzeigt.
	 */
	const hasReminderNote = Boolean(supporter?.autoDeclined) || supporter?.connectionStatus === 'REQUESTED'
	const showRequestJump = canRequest && supporter?.openForNewCompanies && tab !== 'contact'

	const statusBadge = status =>
		status && (
			<span className={clsx(styles.status, styles[`status${status}`])}>{t(`connectionStatus${status}`)}</span>
		)

	return (
		<div className={styles.overlay} {...backdropProps}>
			<div className={styles.modal}>
				{!supporter ? (
					<div className={styles.loading}>
						<Loading />
					</div>
				) : (
					<>
						<header className={styles.header}>
							<AuthAvatar
								hashName={supporter.avatarHashName}
								className={styles.avatar}
								fallback={<Avatar name={supporter.displayName || '?'} size="56" round />}
							/>
							<div className={styles.headerText}>
								<h2 className={styles.name}>{supporter.displayName}</h2>
								{supporter.position && <span className={styles.position}>{supporter.position}</span>}
								{supporter.company && <span className={styles.company}>{supporter.company}</span>}
							</div>
							{/*
							 * Der Status steht im Kopf und damit in jedem Reiter — er ist
							 * der Rahmen für alles andere im Fenster.
							 */}
							{statusBadge(supporter.connectionStatus)}
							<button type="button" className={styles.close} onClick={onClose}>
								×
							</button>
						</header>

						<div className={styles.tabs} ref={tabsRef}>
							{TABS.map(item => (
								<button
									key={item.key}
									type="button"
									className={clsx(styles.tab, tab === item.key && styles.tabActive)}
									onClick={() => setTab(item.key)}
								>
									{t(item.label)}
								</button>
							))}
						</div>

						<div className={styles.body}>
							{tab === 'profile' && (
								<>
									{/*
									 * Von der eigenen Junior Company eingeladen: Das erklärt,
									 * warum diese Person hier steht und in keiner Landesliste —
									 * und dass ihr Profil noch dünn sein darf.
									 */}
									{invitedByOwnCompany && (
										<Card>
											<p className={styles.text}>
												{t('invitedByOwnCompany', { company: invitedByOwnCompany.name })}
											</p>
										</Card>
									)}

									<Card title={t('aboutPerson')}>
										{supporter.bio ? (
											<p className={styles.text}>{supporter.bio}</p>
										) : (
											<p className={styles.hint}>—</p>
										)}
										{supporter.specializations && (
											<div className={styles.block}>
												<span className={styles.blockLabel}>{t('specializations')}</span>
												<p className={styles.text}>{supporter.specializations}</p>
											</div>
										)}
									</Card>

									{Boolean(supporter.competences?.length) && (
										<Card title={t('competences')}>
											<div className={styles.tags}>
												{supporter.competences.map(item => (
													<span key={item} className={styles.tag}>
														{item}
													</span>
												))}
											</div>
										</Card>
									)}

									{Boolean(supporter.trainingOffers?.length) && (
										<Card title={t('trainingPortfolio')}>
											<div className={styles.offers}>
												{supporter.trainingOffers.map(offer => (
													<div key={offer.id} className={styles.offer}>
														<strong className={styles.offerTitle}>{offer.title}</strong>
														<span className={styles.offerMeta}>
															{offer.durationHours ? `${offer.durationHours} h · ` : ''}
															{t(`format${offer.format}`)}
														</span>
													</div>
												))}
											</div>
										</Card>
									)}
								</>
							)}

							{tab === 'target' && (
								<Card title={t('targetAudience')} hint={t('targetAudienceHint')}>
									{/*
									 * Woran es an der eigenen Junior Company scheitert. Die
									 * Anfrage bleibt moeglich — die Zielgruppe ist eine
									 * Vorliebe, kein Verbot.
									 */}
									{Boolean(supporter.matchMisses?.length) && (
										<p className={styles.mismatch}>
											{t('doesNotMatch', {
												criteria: supporter.matchMisses
													.map(key => t(`matchCriterion${key}`))
													.join(', ')
											})}
										</p>
									)}

									<TagList
										label={t('states')}
										values={(supporter.targetStates || []).map(stateLabel)}
									/>
									<TagList label={t('schoolTypes')} values={supporter.targetSchoolTypes} />
									<TagList
										label={t('industries')}
										values={(supporter.targetIndustries || []).map(value =>
											optionLabel(branches, value, i18n.language)
										)}
									/>
									{![
										supporter.targetStates,
										supporter.targetSchoolTypes,
										supporter.targetIndustries
									].some(list => list?.length) && <p className={styles.hint}>—</p>}
								</Card>
							)}

							{tab === 'contact' && (
								<>
									{/*
									 * Die Erreichbarkeit steht bewusst vor den Kontaktdaten und
									 * ist auch ohne Verbindung sichtbar: Wer nur abends und nur
									 * per Video Zeit hat, sagt das, bevor angefragt wird.
									 */}
									<Card title={t('contactPreferences')}>
										{supporter.contactModes?.length || supporter.contactTimes?.length ? (
											<>
												<TagList
													label={t('contactModes')}
													values={(supporter.contactModes || []).map(mode =>
														t(`contactMode${mode}`)
													)}
												/>
												<TagList
													label={t('contactTimes')}
													values={(supporter.contactTimes || []).map(time =>
														t(`contactTime${time}`)
													)}
												/>
											</>
										) : (
											<p className={styles.hint}>{t('noContactPreferences')}</p>
										)}
									</Card>

									{/*
									 * Kontaktdaten gibt es erst nach einer angenommenen Anfrage
									 * (Anforderung #23). Die Schnittstelle liefert sie auch nur
									 * dann — der Status wird hier trotzdem geprüft: Zwei Stellen,
									 * die dasselbe verhindern, sind hier die Mühe wert.
									 */}
									<Card title={t('contactDetails')}>
										{isConnected && (supporter.email || supporter.phone || supporter.website) ? (
											<>
												<div className={styles.contact}>
													{supporter.email && (
														<a
															className={styles.contactLink}
															href={`mailto:${supporter.email}`}
														>
															{supporter.email}
														</a>
													)}
													{supporter.phone && (
														<a
															className={styles.contactLink}
															href={`tel:${supporter.phone}`}
														>
															{supporter.phone}
														</a>
													)}
													{supporter.website && (
														<a
															className={styles.contactLink}
															href={supporter.website}
															target="_blank"
															rel="noreferrer"
														>
															{supporter.website}
														</a>
													)}
												</div>
												{supporter.contactNote && (
													<div className={styles.block}>
														<span className={styles.blockLabel}>{t('contactNote')}</span>
														<p className={styles.text}>{supporter.contactNote}</p>
													</div>
												)}
											</>
										) : (
											<p className={styles.hint}>
												{supporter.connectionStatus === 'REQUESTED'
													? t('requestPendingHint')
													: t('contactHiddenHint')}
											</p>
										)}
									</Card>

									{canRequest &&
										(supporter.openForNewCompanies ? (
											<Card title={t('makeRequest')}>
												<div className={styles.requestArea}>
													{needsCompanyChoice && (
														<label className={styles.companyChoice}>
															<span className={styles.blockLabel}>
																{t('requestForCompany')}
															</span>
															<select
																className={styles.companySelect}
																value={companyId}
																onChange={e => setCompanyId(e.target.value)}
															>
																<option value="">{t('chooseCompany')}</option>
																{availableCompanies.map(c => (
																	<option key={c.id} value={c.id}>
																		{c.name}
																	</option>
																))}
															</select>
														</label>
													)}
													<div>
														<PlainTextarea
															label={t('requestMessage')}
															rows={4}
															value={message}
															maxLength={2000}
															placeholder={t('requestMessagePlaceholder')}
															onChange={e => setMessage(e.target.value)}
														/>
														<p className={styles.fieldHint}>
															{messageMissing > 0
																? t('requestMessageMissing', { count: messageMissing })
																: t('requestMessageHint')}
														</p>
													</div>
													{Boolean(supporter.competences?.length) && (
														<div>
															<span className={styles.blockLabel}>
																{t('requestCompetencesLabel')}
															</span>
															<div className={styles.tags}>
																{supporter.competences.map(item => (
																	<button
																		key={item}
																		type="button"
																		aria-pressed={chosenCompetences.includes(item)}
																		className={clsx(
																			styles.tagChoice,
																			chosenCompetences.includes(item) &&
																				styles.tagChosen
																		)}
																		onClick={() => toggleCompetence(item)}
																	>
																		{item}
																	</button>
																))}
															</div>
														</div>
													)}
													<p className={styles.nextStep}>
														{supporter.contactDays
															? t('requestNextStepHintDays', {
																	days: supporter.contactDays
															  })
															: t('requestNextStepHint')}
													</p>
													<Btn
														disabled={
															isSending ||
															messageMissing > 0 ||
															(needsCompanyChoice && !companyId)
														}
														onClick={request}
													>
														{t('sendRequest')}
													</Btn>
												</div>
											</Card>
										) : (
											<Card>
												<p className={styles.hint}>{t('notOpenForNewCompanies')}</p>
											</Card>
										))}
								</>
							)}

							{tab === 'collaboration' && (
								<>
									<Card title={t('collaboration')}>
										{/*
										 * Eine Lehrkraft betreut oft mehrere Firmen — dann steht
										 * hier je Firma eine Zeile, statt eines Status, der nicht
										 * sagt, um welche Firma es geht (#24).
										 */}
										{connections.length ? (
											<div className={styles.connectionList}>
												{connections.map(c => (
													<span key={c.companyId} className={styles.connectionRow}>
														<span className={styles.connectionCompany}>
															{c.companyName}
														</span>
														{statusBadge(c.status)}
													</span>
												))}
											</div>
										) : supporter.connectionStatus ? (
											<div className={styles.connectionList}>
												<span className={styles.connectionRow}>
													{statusBadge(supporter.connectionStatus)}
												</span>
											</div>
										) : (
											<p className={styles.hint}>{t('noCollaborationYet')}</p>
										)}

										{!supporter.connectionStatus && (
											<p className={styles.hint}>{t('collaborationHowItWorks')}</p>
										)}
									</Card>

									{/*
									 * Beenden ist selten und wirkt auf die andere Seite. Es steht
									 * deshalb in einem eigenen Reiter mit der Erklärung daneben,
									 * wann es angebracht ist und was dabei passiert — und nicht
									 * als kleiner Textlink neben dem Status.
									 */}
									{Boolean(endableConnections.length) && (
										<Card title={t('endCollaboration')}>
											<div className={styles.block}>
												<span className={styles.blockLabel}>{t('endCollaborationWhen')}</span>
												<p className={styles.text}>{t('endCollaborationWhenText')}</p>
											</div>
											<div className={styles.block}>
												<span className={styles.blockLabel}>{t('endCollaborationEffect')}</span>
												<ul className={styles.effectList}>
													<li>{t('endCollaborationEffect1')}</li>
													<li>{t('endCollaborationEffect2')}</li>
													<li>{t('endCollaborationEffect3')}</li>
													<li>{t('endCollaborationEffect4')}</li>
												</ul>
											</div>
											<div className={styles.endActions}>
												{endableConnections.map((c, index) => (
													<Btn
														key={c.companyId || index}
														theme="red"
														disabled={isSending}
														onClick={() => endCollaboration(c)}
													>
														{c.companyName
															? `${t('endCollaboration')}: ${c.companyName}`
															: t('endCollaboration')}
													</Btn>
												))}
											</div>
										</Card>
									)}
								</>
							)}

							{tab === 'log' && (
								<Card title={t('logTab')} hint={t('supporterCompanyLogHint')}>
									{logCompanies.length ? (
										<>
											{/*
											 * Eine Lehrkraft betreut mehrere Junior Companies, und
											 * jede führt ihren eigenen Verlauf. Ohne diese Auswahl
											 * landete eine Anmerkung bei einer beliebigen davon.
											 */}
											{logCompanies.length > 1 && (
												<label className={styles.companyChoice}>
													<span className={styles.blockLabel}>{t('logForCompany')}</span>
													<select
														className={styles.companySelect}
														value={logCompanyId}
														onChange={e => setLogCompanyId(e.target.value)}
													>
														{logCompanies.map(c => (
															<option key={c.id} value={c.id}>
																{/*
																 * Der Status hinter dem Namen: Sonst
																 * sind die Firmen ununterscheidbar und
																 * es bleibt Raten, hinter welcher der
																 * Verlauf steckt.
																 */}
																{c.status
																	? `${c.name} — ${t(`connectionStatus${c.status}`)}`
																	: `${c.name} — ${t('noRequestYet')}`}
															</option>
														))}
													</select>
												</label>
											)}

											<RecordComments
												entityType={RECORD_TYPES.SUPPORTER_COMPANY}
												entityId={logCompanyId}
												url={TApi.COMMON_SUPPORTER_LOG(supporterId, logCompanyId)}
												hint={t('supporterCompanyLogNoteHint')}
											/>
										</>
									) : (
										<p className={styles.hint}>{t('noCompanyForLog')}</p>
									)}
								</Card>
							)}
						</div>

						{(hasReminderNote || showRequestJump) && (
							<footer className={styles.footer}>
								<ReminderNote
									status={supporter.connectionStatus}
									reminderCount={supporter.reminderCount}
									lastReminderAt={supporter.lastReminderAt}
									autoDeclined={supporter.autoDeclined}
								/>
								{/*
								 * Der Weg zur Anfrage bleibt aus jedem Reiter sichtbar; gesendet
								 * wird sie im Reiter "Kontakt", wo die Begleitnachricht steht.
								 */}
								{showRequestJump && (
									<Btn theme="secondary" onClick={() => setTab('contact')}>
										{t('requestExpert')}
									</Btn>
								)}
							</footer>
						)}
					</>
				)}
			</div>
		</div>
	)
}
